import './notifications.css';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Button } from '@openai/apps-sdk-ui/components/Button';
import { Checkbox } from '@openai/apps-sdk-ui/components/Checkbox';
import { Input } from '@openai/apps-sdk-ui/components/Input';
import { Badge } from '@openai/apps-sdk-ui/components/Badge';
import { api, APIError, errorMessage } from './api';
import { ErrorNotice, Field } from './ui';
import { validatedTimeZone } from '../shared/datetime';
import type {
  NotificationPreferences as Preferences,
  NotificationSubscription as Registration,
  Notifications as NotificationSettings,
  MorningDigest as Digest,
} from '../shared/types';
import {
  browserNotificationIssue,
  browserPermission,
  clearBrowserBinding,
  enableBrowserPush,
  existingBrowserSubscription,
  readBrowserBinding,
  writeBrowserBinding,
} from './notifications-client';

export function NotificationsPanel({
  userId,
  isCurrentAccount,
}: {
  userId: string;
  isCurrentAccount: () => boolean;
}) {
  const [data, setData] = useState<NotificationSettings | null>(null);
  const [draft, setDraft] = useState<Preferences | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [currentBrowser, setCurrentBrowser] = useState<string | null>(null);
  const [permission, setPermission] = useState(browserPermission);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState('This browser');
  const mounted = useRef(true);
  const serial = useRef(0);
  const running = useRef(false);
  const account = useRef(isCurrentAccount);
  account.current = isCurrentAccount;
  const valid = () => mounted.current && account.current();
  async function load() {
    const request = ++serial.current;
    try {
      const [result, subscription] = await Promise.all([
        api<NotificationSettings>('/notifications'),
        existingBrowserSubscription().catch(() => null),
      ]);
      if (!valid() || request !== serial.current) return;
      setData(result);
      if (!draftRef.current) {
        draftRef.current = result.preferences;
        setDraft(result.preferences);
      }
      const binding = readBrowserBinding(userId);
      setCurrentBrowser(
        subscription && result.subscriptions.some((row) => row.id === binding) ? binding : null,
      );
      setPermission(browserPermission());
      setError('');
    } catch (error) {
      if (valid() && request === serial.current) setError(errorMessage(error));
    } finally {
      if (valid() && request === serial.current) setLoading(false);
    }
  }
  useEffect(() => {
    mounted.current = true;
    void load();
    if (location.hash === '#notifications')
      requestAnimationFrame(() =>
        document.getElementById('notifications')?.scrollIntoView({ block: 'start' }),
      );
    const focus = () => {
      if (valid()) setPermission(browserPermission());
    };
    window.addEventListener('focus', focus);
    return () => {
      mounted.current = false;
      serial.current++;
      window.removeEventListener('focus', focus);
    };
  }, [userId]);
  function change<K extends keyof Preferences>(key: K, value: Preferences[K]) {
    setDraft((previous) => (previous ? { ...previous, [key]: value } : previous));
    setNotice('');
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!draft || running.current || !valid()) return;
    setError('');
    setNotice('');
    let timeZone: string;
    try {
      timeZone = validatedTimeZone(draft.timeZone);
    } catch (error) {
      setError(errorMessage(error));
      return;
    }
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(draft.morningTime)) {
      setError('Choose a valid morning digest time.');
      return;
    }
    running.current = true;
    serial.current++;
    setBusy(true);
    try {
      const { version, ...fields } = draft;
      const result = await api<{ preferences: Preferences }>(
        '/notifications/preferences',
        'PATCH',
        { ...fields, timeZone, expectedVersion: version },
      );
      if (!valid()) return;
      serial.current++;
      draftRef.current = result.preferences;
      setDraft(result.preferences);
      setData((previous) =>
        previous ? { ...previous, preferences: result.preferences } : previous,
      );
      setNotice('Notification preferences saved.');
    } catch (error) {
      if (valid()) {
        if (error instanceof APIError && error.status === 409) await load();
        if (valid()) setError(errorMessage(error));
      }
    } finally {
      running.current = false;
      if (valid()) setBusy(false);
    }
  }
  async function enable() {
    if (!data?.available || !data.publicKey || running.current || !valid()) return;
    running.current = true;
    serial.current++;
    setBusy(true);
    setError('');
    setNotice('');
    let subscription: PushSubscription | null = null;
    let accepted = false;
    try {
      const enabled = await enableBrowserPush(data.publicKey, valid);
      subscription = enabled.subscription;
      if (!valid()) return;
      const result = await api<{ subscription: Registration }>(
        '/notifications/subscriptions',
        'POST',
        { ...enabled.payload, label: label.trim() || 'This browser' },
      );
      accepted = true;
      if (!valid()) {
        await subscription.unsubscribe().catch(() => false);
        return;
      }
      writeBrowserBinding(userId, result.subscription.id);
      serial.current++;
      setCurrentBrowser(result.subscription.id);
      setPermission(browserPermission());
      setData((previous) =>
        previous
          ? {
              ...previous,
              subscriptions: [
                result.subscription,
                ...previous.subscriptions.filter((row) => row.id !== result.subscription.id),
              ],
            }
          : previous,
      );
      setDraft((previous) => (previous ? { ...previous, enabled: true } : previous));
      let cleanupWarning = '';
      if (currentBrowser && currentBrowser !== result.subscription.id && valid()) {
        try {
          await api(`/notifications/subscriptions/${currentBrowser}`, 'DELETE');
          if (valid())
            setData((previous) =>
              previous
                ? {
                    ...previous,
                    subscriptions: previous.subscriptions.filter(
                      (row) => row.id !== currentBrowser,
                    ),
                  }
                : previous,
            );
        } catch {
          cleanupWarning =
            ' The previous registration could not be removed; remove it from Registered browsers when the connection is available.';
        }
      }
      if (!valid()) return;
      setNotice(
        (data.preferences.enabled
          ? 'Browser registered for this sign-in. Your saved alert preferences apply.'
          : 'Browser registered. Choose your alerts and save preferences to enable delivery.') +
          cleanupWarning,
      );
    } catch (error) {
      if (subscription && !accepted) await subscription.unsubscribe().catch(() => false);
      if (valid()) {
        setPermission(browserPermission());
        setError(errorMessage(error));
      }
    } finally {
      running.current = false;
      if (valid()) setBusy(false);
    }
  }
  async function remove(id: string) {
    if (running.current || !valid()) return;
    running.current = true;
    serial.current++;
    setBusy(true);
    setError('');
    setNotice('');
    const subscription =
      id === currentBrowser ? await existingBrowserSubscription().catch(() => null) : null;
    try {
      if (!valid()) return;
      await api(`/notifications/subscriptions/${id}`, 'DELETE');
      if (!valid()) return;
      serial.current++;
      setData((previous) =>
        previous
          ? { ...previous, subscriptions: previous.subscriptions.filter((row) => row.id !== id) }
          : previous,
      );
      if (id === currentBrowser) {
        clearBrowserBinding(userId);
        setCurrentBrowser(null);
      }
      const removed = subscription ? await subscription.unsubscribe().catch(() => false) : true;
      if (valid())
        setNotice(
          removed
            ? 'Browser registration removed.'
            : 'The server registration was removed. Browser cleanup could not finish; notifications for this registration are disabled.',
        );
    } catch (error) {
      if (valid()) setError(errorMessage(error));
    } finally {
      running.current = false;
      if (valid()) setBusy(false);
    }
  }
  const issue = browserNotificationIssue();
  const conflict = !!draft && !!data && draft.version !== data.preferences.version;
  return (
    <section className="settings-section notifications-section" id="notifications">
      <div className="settings-section-heading">
        <h2>Notifications</h2>
        <p>
          Optional browser alerts and a private morning digest. Delivery depends on your browser and
          device settings.
        </p>
      </div>
      {loading && (
        <p className="muted small" role="status">
          Loading notification settings…
        </p>
      )}
      {error && <ErrorNotice message={error} retry={() => void load()} />}
      {notice && (
        <p className="notification-notice" role="status">
          {notice}
        </p>
      )}
      {data && (
        <>
          <div className="notification-browser-status">
            <Badge color={data.available ? 'secondary' : 'warning'}>
              {data.available ? 'Browser delivery configured' : 'Browser delivery not configured'}
            </Badge>
            <span>
              {permission === 'denied'
                ? 'Permission blocked'
                : permission === 'granted'
                  ? 'Browser permission granted'
                  : permission === 'default'
                    ? 'Permission not requested'
                    : 'Browser notifications unavailable'}
            </span>
          </div>
          {!data.available && (
            <p className="notification-help">
              Delivery has not been configured for this workspace. Save alert choices now; enable
              notifications after delivery is configured.
            </p>
          )}
          {issue && <p className="notification-help">{issue}</p>}
          {permission === 'denied' && (
            <p className="notification-help">
              Notifications are blocked for this site. Change the permission in your browser
              settings, then return here.
            </p>
          )}
          <div className="notification-browser-controls">
            <Field label="Browser name">
              <Input
                aria-label="Notification browser name"
                maxLength={80}
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                readOnly={busy}
              />
            </Field>
            <Button
              color="secondary"
              variant="outline"
              disabled={
                busy || !!issue || permission === 'denied' || !data.available || !data.publicKey
              }
              onClick={() => void enable()}
            >
              {currentBrowser ? 'Reconnect this browser' : 'Enable this browser'}
            </Button>
            {currentBrowser && (
              <Button
                color="secondary"
                variant="ghost"
                disabled={busy}
                onClick={() => void remove(currentBrowser)}
              >
                Disable this browser
              </Button>
            )}
          </div>
          {currentBrowser && (
            <p className="notification-help">
              This browser has a saved registration. Reconnect after signing in again. Browser
              registrations never transfer automatically between accounts.
            </p>
          )}
          {draft && (
            <form className="notification-preferences" onSubmit={save} aria-busy={busy}>
              <Checkbox
                label="Enable notifications"
                checked={draft.enabled}
                disabled={busy || (!data.available && !draft.enabled)}
                onCheckedChange={(value) => change('enabled', value)}
              />
              {!draft.enabled && (
                <p className="notification-help">
                  {data.preferences.enabled
                    ? 'Save preferences to disable notifications for your account.'
                    : 'Notifications are disabled for your account.'}
                </p>
              )}
              <Checkbox
                label="Due reminder and follow-up alerts"
                checked={draft.dueAlerts}
                disabled={busy}
                onCheckedChange={(value) => change('dueAlerts', value)}
              />
              <Checkbox
                label="Advance alerts"
                checked={draft.advanceAlerts}
                disabled={busy}
                onCheckedChange={(value) => change('advanceAlerts', value)}
              />
              <Field label="Alert in advance">
                <select
                  aria-label="Advance notification time"
                  value={draft.advanceMinutes}
                  disabled={busy || !draft.advanceAlerts}
                  onChange={(event) =>
                    change('advanceMinutes', Number(event.target.value) as 60 | 1440)
                  }
                >
                  <option value={60}>1 hour before</option>
                  <option value={1440}>1 day before</option>
                </select>
              </Field>
              <p className="notification-help">
                Advance alerts include your assigned tasks and tasks you created that are
                unassigned. Date-only task deadlines use 09:00 in your notification timezone.
              </p>
              <Checkbox
                label="Morning digest"
                checked={draft.morningDigest}
                disabled={busy}
                onCheckedChange={(value) => change('morningDigest', value)}
              />
              <div className="notification-schedule">
                <Field label="Morning time">
                  <Input
                    aria-label="Morning digest time"
                    type="time"
                    value={draft.morningTime}
                    onChange={(event) => change('morningTime', event.target.value)}
                    required
                    readOnly={busy}
                    disabled={!draft.morningDigest}
                  />
                </Field>
                <Field label="Notification timezone">
                  <Input
                    aria-label="Notification timezone"
                    value={draft.timeZone}
                    onChange={(event) => change('timeZone', event.target.value)}
                    maxLength={100}
                    required
                    readOnly={busy}
                    placeholder="Europe/London"
                  />
                </Field>
              </div>
              <p className="notification-help">
                The digest is a saved count of your work, shown on Today. Lock-screen alerts contain
                only a generic update and open Today; task titles, private text, and message
                contents are not sent in the alert.
              </p>
              {conflict && (
                <div className="version-conflict" role="status">
                  <p>
                    Preferences changed elsewhere. Your edits are preserved. Latest saved timezone:{' '}
                    {data.preferences.timeZone}; notifications{' '}
                    {data.preferences.enabled ? 'enabled' : 'disabled'}.
                  </p>
                  <p>
                    Due alerts: {data.preferences.dueAlerts ? 'on' : 'off'}. Advance alerts:{' '}
                    {data.preferences.advanceAlerts
                      ? data.preferences.advanceMinutes === 60
                        ? '1 hour before'
                        : '1 day before'
                      : 'off'}
                    . Morning digest:{' '}
                    {data.preferences.morningDigest
                      ? `on at ${data.preferences.morningTime}`
                      : 'off'}
                    .
                  </p>
                  <Button
                    color="secondary"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      setDraft((previous) =>
                        previous ? { ...previous, version: data.preferences.version } : previous,
                      )
                    }
                  >
                    Keep my edits and use latest version
                  </Button>
                </div>
              )}
              <Button type="submit" color="primary" loading={busy} disabled={conflict}>
                Save notification preferences
              </Button>
            </form>
          )}
          <div className="notification-registrations">
            <h3>Registered browsers</h3>
            {data.subscriptions.length ? (
              data.subscriptions.map((row) => (
                <div key={row.id}>
                  <span>
                    <strong>{row.label || 'Browser'}</strong>
                    <small>
                      Registered {new Date(row.createdAt).toLocaleDateString()}
                      {row.id === currentBrowser ? ' · this browser' : ''}
                    </small>
                  </span>
                  <Button
                    color="secondary"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => void remove(row.id)}
                  >
                    Remove<span className="sr-only"> {row.label || 'browser'} registration</span>
                  </Button>
                </div>
              ))
            ) : (
              <p className="notification-help">No registered browsers.</p>
            )}
          </div>
        </>
      )}
    </section>
  );
}

export function MorningDigest() {
  const [digest, setDigest] = useState<Digest | null>(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    let serial = 0;
    const load = async () => {
      const request = ++serial;
      try {
        const result = await api<{ digest: Digest | null }>('/notifications/digest');
        if (active && request === serial) {
          setDigest(result.digest);
          setError('');
        }
      } catch (error) {
        if (active && request === serial) setError(errorMessage(error));
      }
    };
    void load();
    const visible = () => {
      if (!document.hidden) void load();
    };
    document.addEventListener('visibilitychange', visible);
    const timer = setInterval(visible, 60000);
    return () => {
      active = false;
      serial++;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [retry]);
  if (error)
    return (
      <ErrorNotice
        message={`Morning digest unavailable. ${error}`}
        retry={() => setRetry((value) => value + 1)}
      />
    );
  if (!digest) return null;
  return (
    <section className="morning-digest" aria-label="Latest morning digest">
      <div>
        <h2>Latest morning digest</h2>
        <p>
          Generated{' '}
          {new Intl.DateTimeFormat([], {
            dateStyle: 'medium',
            timeStyle: 'short',
            timeZone: digest.timeZone,
          }).format(new Date(digest.generatedAt))}{' '}
          · {digest.timeZone}
        </p>
      </div>
      <dl>
        {[
          ['Overdue tasks', digest.overdueTasks],
          ['Tasks today', digest.todayTasks],
          ['Reminders', digest.reminders],
          ['Waiting', digest.waiting],
        ].map(([label, count]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{count}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
